import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import '@aws-amplify/ui-react/styles.css';
import 'rsuite/dist/rsuite.min.css';
import './css/spacing.css';
import './css/main.css';
import './css/custom.css';
import './css/sizing.css';
import './css/tables.css';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import store, { persistor } from './store/store';
import App from './App';
import { axe } from './common/a11y';
import { captureExceptionWithContext } from './common/sentryUtils';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import ErrorPage from './pages/ErrorPage';
import { InnerError } from './classes/InnerError';
import { PersistGate } from 'redux-persist/integration/react';

const root = ReactDOM.createRoot(document.getElementById('root'));

Sentry.init({
  dsn: 'https://0eee184cdfa047a19d101cf145eed375@o323940.ingest.sentry.io/6139124',
  environment: window.location.hostname,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      /**
       * By default, Sentry will send a header: Access-Control-Request-Headers: sentry-trace
       * However, http-server is not capable of receiving this header and nor are we able
       *  to config http-server to be able to receive this header
       * So in order to not attach the request when requesting the JSON from http-server
       *  we use a regex this specific regex that tests if 'localhost' is in the URL or not
       */
      tracePropagationTargets: [/^(?!.*?localhost)/],
    }),
    Sentry.replayIntegration(),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  beforeSend: (event) => {
    // Add a console.log here for "event" to see all events being sent to Sentry
    return event;
  },
});

window.addEventListener('unhandledrejection', function (promiseRejectionEvent) {
  class UnhandledRejectionError extends Error {
    constructor(message) {
      super(message);
      this.name = 'UnhandledRejectionError';
    }
  }

  const reason = JSON.stringify(promiseRejectionEvent.reason.toJSON());
  captureExceptionWithContext(new UnhandledRejectionError(), { reason });
});

const TopLevelFallback = ({ error }) => {
  useEffect(() => {
    if (error instanceof InnerError) {
      throw error;
    }
  }, [error]);

  return (
    <ErrorPage
      errorHeader="Oops! Something went wrong."
      showBackButton={false}
    />
  );
};

root.render(
  <React.StrictMode>
    <BrowserRouter basename="/">
      <Provider store={store}>
        <PersistGate
          loading={null}
          persistor={persistor}
        >
          <Sentry.ErrorBoundary fallback={<TopLevelFallback />}>
            <App />
          </Sentry.ErrorBoundary>
        </PersistGate>
      </Provider>
    </BrowserRouter>
  </React.StrictMode>
);

axe(React, ReactDOM, {
  enabled:
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_ENVIRONMENT === 'dev' &&
    // eslint-disable-next-line no-undef
    process.env.REACT_APP_RUN_AXE === 'enabled',
  delay: 10000,
});
